import React, { useEffect, useRef } from 'react';
import { Animated, TouchableOpacity, Text, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';

interface ToastState {
  text: string;
  undoKey?: string;
}

interface Props {
  toast: ToastState | null;
  onUndo?: (key: string) => void;
  bottomOffset?: number;
  /** Called once the toast has had its time — without it the pill never leaves. */
  onDismiss?: () => void;
  dismissAfterMs?: number;
}

export function Toast({ toast, onUndo, bottomOffset = 84, onDismiss, dismissAfterMs = 5000 }: Props) {
  const { t } = useTranslation();
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (toast) {
      Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }).start();
    } else {
      Animated.timing(opacity, { toValue: 0, duration: 200, useNativeDriver: true }).start();
    }
  }, [toast]);

  // Auto-dismiss: the toast sits over the bottom of the week list, so leaving
  // it up until the next action hides content.
  useEffect(() => {
    if (!toast || !onDismiss) return;
    const timer = setTimeout(onDismiss, dismissAfterMs);
    return () => clearTimeout(timer);
  }, [toast, onDismiss, dismissAfterMs]);

  if (!toast) return null;

  return (
    <Animated.View style={[styles.pill, { bottom: bottomOffset, opacity }]} testID="toast">
      <Text style={styles.text}>{toast.text}</Text>
      {!!toast.undoKey && onUndo && (
        <TouchableOpacity onPress={() => onUndo(toast.undoKey!)} style={styles.undoBtn} testID="toast-undo">
          <Text style={styles.undoText}>{t('common.undo')}</Text>
        </TouchableOpacity>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  pill: {
    position: 'absolute',
    alignSelf: 'center',
    left: 16,
    right: 16,
    backgroundColor: Paprika.textPrimary,
    borderRadius: 24,
    paddingHorizontal: 20,
    paddingVertical: 13,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // warm shadow
    shadowColor: 'rgba(82, 68, 60, 0.18)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 8,
  },
  text: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.surfaceLowest,
    flex: 1,
  },
  undoBtn: {
    marginLeft: 12,
  },
  undoText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.primary,
  },
});
